import * as React from 'react';
import { observer } from 'mobx-react';
import { ICountryBudgetDecision } from 'typings/countryBudgetDecision';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { Employee } from 'nomst/Stores/Decision/employee';
import Collapse from 'components/Common/collapse';
import CountryBudgetCard from 'nomst/Components/Decision/countryBudgetCard';
import EmployeeCard from 'nomst/Components/Decision/employeeCard';
import EmployeeCardDetails from 'nomst/Components/Decision/EmployeeCardDetails/employeeCardDetails';
import ConversationBox from 'nomst/Components/Decision/conversationBox';
import { User } from 'nomst/Stores/User/user';
import { AmountCode } from 'typings/enums';
import { cycleRules } from 'utils/MobxDataManager/singletons';
import './countryBudgetDecision.scss';
import IneligibleEmployeeHiddenView from './ineligibleEmployeeHiddenView';
import { CountryBudgetDecisionDispatcher } from 'nomst/Stores/Decision/countryBudgetDecision.dispatcher';

export interface ICountryBudgetDecisionProps {
    decisionDispatcher: DecisionDispatcher;
    countryBudgetDecision: ICountryBudgetDecision;
    configuration: Configuration;
    user: User;
    useCollapse: boolean;
    showExpandedByDefault: boolean;
    isDirectsOnly: boolean;
    selectedCurrency?: string;
}

export interface ICountryBudgetDecisionState {
    isFetching: boolean;
}

export class CountryBudgetDecision extends React.Component<ICountryBudgetDecisionProps, ICountryBudgetDecisionState> {
    private _observer: Nullable<IntersectionObserver>;

    constructor(props: ICountryBudgetDecisionProps) {
        super(props);
        this.handleShowMeMoreEmployeesClick = this.handleShowMeMoreEmployeesClick.bind(this);
        this.showUserMoreEntries = this.showUserMoreEntries.bind(this);
        this.state = { isFetching: false };
    }

    componentDidMount(): void {
        this.setObserver();
    }

    componentDidUpdate(): void {
        if (!this._observer && !this.hideShowMoreButton()) {
            this.setObserver();
        }
    }

    render(): React.ReactNode {
        const { countryBudgetDecision, showExpandedByDefault, useCollapse } = this.props;
        if (!countryBudgetDecision) {
            return null;
        }

        if (!useCollapse) {
            return this.renderCountryBudgetDecision();
        }

        return (
            <Collapse
                key={countryBudgetDecision.name}
                title={this.renderCollapseTitle(
                    countryBudgetDecision.name,
                    countryBudgetDecision.count)}
                showChildrenByDefault={showExpandedByDefault}
            >
                {this.renderCountryBudgetDecision()}
            </Collapse>
        );
    }

    private setObserver(): void {
        // Asynchronously observe changes in the intersection of a target element
        // with the top-level document's viewport
        if (typeof IntersectionObserver === 'undefined') {
            return;
        }
        this._observer = new IntersectionObserver(this.showUserMoreEntries, {
            threshold: 1 // 100% of target element needs to be in view port
        });
        const target = document.querySelector('.show-me-more__button');
        if (!target) {
            return;
        }
        this._observer.observe(target);
    }

    private renderCollapseTitle(countryName: string, employeesCount: number): React.ReactNode {
        return (
            <span className="collapse-title">
                <i className="icon-location" />
                <span>{countryName} ({employeesCount})</span>
            </span>
        );
    }

    private renderCountryBudgetDecision(): React.ReactNode {
        return (
            <div className="by-country-budget-container">
                <div className="budgets-column">
                    {this.renderCountryBudgetCards()}
                </div>
                <div className="employees-column">
                    {this.renderEmployeeListContainer()}
                    {this.renderShowMeMoreButton()}
                </div>
            </div>
        );
    }

    private renderCountryBudgetCards(): React.ReactNode {
        const { configuration, countryBudgetDecision } = this.props;

        if (!countryBudgetDecision.employees || countryBudgetDecision.employeesCount === 0)
        {
            return null;
        }

        return (
            <div className="budget-list-container">
                <div className="main-budget-header">Budgets</div>
                {
                    countryBudgetDecision.budgets.map(budget => (
                        <CountryBudgetCard
                            key={budget.budgetCycleId}
                            countryBudget={budget}
                            configuration={configuration}
                        />
                    ))
                }
            </div>
        );
    }

    private renderEmployeeListContainer(): React.ReactNode {
        const { countryBudgetDecision, configuration, decisionDispatcher } = this.props;
        const employeeListContainerClasses = countryBudgetDecision.selectedEmployeeSourceId ?
            'employee-list-container show-employee-card-details' :
            'employee-list-container';
        if (!countryBudgetDecision.employees || countryBudgetDecision.employeesCount === 0) {
            return (
                <div className="employee-list-container">
                    <IneligibleEmployeeHiddenView />
                </div>
            );
        }
        return (
            <div className={employeeListContainerClasses}>
                <div className="employee-card-list">
                    {
                        countryBudgetDecision.limitedEmployees.map(employee => (
                            <EmployeeCard
                                key={employee.employeeSourceId}
                                decisionDispatcher={decisionDispatcher}
                                countryDecisionDispatcher={
                                    countryBudgetDecision.countryBudgetDecisionDispatcher ?? {} as CountryBudgetDecisionDispatcher
                                }
                                employee={employee}
                                configuration={configuration}
                                isCycleLocked={this.isCycleLockedForEmployee(employee)}
                                selectedEmployeeId={countryBudgetDecision.selectedEmployeeSourceId}
                            />
                        ))
                    }
                </div>
                {this.renderEmployeeCardDetails()}
                {this.renderConversationBox()}
            </div>
        );
    }

    private renderEmployeeCardDetails(): React.ReactNode {
        const { countryBudgetDecision, configuration, user, selectedCurrency } = this.props;
        if (!countryBudgetDecision.selectedEmployeeSourceId) {
            return null;
        }

        const selectedEmployee = countryBudgetDecision.getEmployee(
            countryBudgetDecision.selectedEmployeeSourceId);
        if (!selectedEmployee) {
            return null;
        }

        const bonusAmount = configuration.getCycleBudgetField(AmountCode.Bonus);
        const hasTargetValue = selectedEmployee.plannedBonus && selectedEmployee.plannedBonus > 0;
        // TODO: PSP-21488 reimplement flag to show component
        const isMultiComponent = false;
        const showMultiComponentBonus = bonusAmount && hasTargetValue && isMultiComponent;

        return (
            <EmployeeCardDetails
                countryDecisionDispatcher={
                    countryBudgetDecision.countryBudgetDecisionDispatcher ?? {} as CountryBudgetDecisionDispatcher
                }
                employee={selectedEmployee}
                configuration={configuration}
                user={user}
                isCycleLocked={this.isCycleLockedForEmployee(selectedEmployee)}
                showMultiComponentBonus={!!showMultiComponentBonus}
                selectedCurrency={selectedCurrency}
            />
        );
    }

    private renderShowMeMoreButton(): React.ReactNode {
        if (this.hideShowMoreButton()) {
            this._observer = null;
            return null;
        }
        return (
            <div className="show-me-more">
                <i className="icon-users"/>
                <button onClick={this.handleShowMeMoreEmployeesClick} className="show-me-more__button pxl-link">
                    Load more employees
                </button>
            </div>
        );
    }

    private hideShowMoreButton(): boolean {
        const { countryBudgetDecision } = this.props;
        return countryBudgetDecision.currentMaxViewableEmployees >= countryBudgetDecision.count ||
            this.state.isFetching || !!countryBudgetDecision.searchedEmployee || !!countryBudgetDecision.filterByJob ||
            !!countryBudgetDecision.filterByDepartment;
    }

    private showUserMoreEntries(entries: IntersectionObserverEntry[]): void {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                this.handleShowMeMoreEmployeesClick();
            }
        });
    }

    private async handleShowMeMoreEmployeesClick(): Promise<void> {
        const { countryBudgetDecision } = this.props;
        if (this.state.isFetching || !!countryBudgetDecision.searchedEmployee || !countryBudgetDecision.employees.length) {
            return;
        }
        this.setState({isFetching: true});
        await countryBudgetDecision.increaseEmployeeViewSize(this.props.isDirectsOnly);
        this.setState({isFetching: false});
    }

    private renderConversationBox(): React.ReactNode {
        const { countryBudgetDecision, configuration, user } = this.props;
        if (!countryBudgetDecision.selectedConversationEntityId) {
            return null;
        }

        const employee = countryBudgetDecision.getEmployee(
            countryBudgetDecision.selectedConversationEntityId);
        if (!employee) {
            return null;
        }

        return (
            <ConversationBox
                countryDecisionDispatcher={countryBudgetDecision.countryBudgetDecisionDispatcher ?? {} as CountryBudgetDecisionDispatcher}
                conversationSummary={employee.conversationSummary}
                configuration={configuration}
                userId={user.userId}
            />
        );
    }

    private isCycleLockedForEmployee(employee: Employee): boolean {
        return cycleRules.isCurrentCycleLocked || !employee.isReviewable;
    }
}

export default observer(CountryBudgetDecision);